/**
 * Punto de entrada de React — Monta App en el DOM con StrictMode
 * Exporta: default export
 *
 * CAPACITOR: Inicializa SplashScreen y StatusBar en plataformas nativas.
 * MSW: ACTIVO POR DEFECTO, incluyendo producción (modo demo). Se desactiva
 * solo con VITE_DISABLE_MSW=true o VITE_DISABLE_MSW_NATIVE=true en APK.
 */
import { logger } from "@/lib/logger";
import React from "react";
import ReactDOM from "react-dom/client";

// Polyfill para @react-pdf/renderer en Vite
if (
  typeof window !== "undefined" &&
  !(window as Window & { process?: object }).process
) {
  (window as Window & { process: object }).process = { env: {} };
}

import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster, ToastBar, toast } from "react-hot-toast";
import { ToastLimit, ToastFitWidth } from "./components/common/toastUi";
import { X } from "lucide-react";
import App from "./App";
import "./index.css";
import { installApiConnectivityListeners } from "./services/apiConnectivity";
import { APP_TOASTER_CLASS, appToasterOptions } from "./lib/appToast";
import { initNativeAppShell, syncNativeStatusBar } from "./lib/nativeShell";
import { unregisterConflictingServiceWorkers } from "./lib/dev/unregisterConflictingServiceWorkers";
import {
  isReloadLoopDetected,
  resetReloadLoopGuard,
} from "./lib/pageLoadError";
import { applyCajaDemoFreshStart } from "./features/caja/demo/applyCajaDemoFreshStart";
import { hasUserRealCajaActivity } from "./features/caja/demo/demoJornadaLifecycle";
import {
  reconstructMissingMovimientosFromAuditoria,
  reconstructMissingVentasFromAuditoria,
} from "./features/caja/utils/reconstructCajaFromAuditoria";
import { runIntegrityCheck } from "./lib/persistence/integrityCheck";
import { restoreFromIDBMirror } from "./lib/persistence/idbMirrorBackend";
import { defaultProducts } from "@/mocks/data/products";
// Side-effect: registra window.__altenotHealth para soporte técnico en campo.
import "./lib/persistence/diagnostics";
import {
  applyMockDemoBootstrap,
  MOCK_DATA_RESET_KEYS,
} from "./lib/demo/applyMockDemoBootstrap";
import { recoverStore } from "./lib/persistence/transactionalStore";

initNativeAppShell();

// Hidratar tema al cargar (antes del primer render)
import { useThemeStore } from "./store/themeStore";
useThemeStore.getState(); // dispara onRehydrate → aplica clase 'dark' al <html>

// ── Inicialización nativa Capacitor ────────────────────────────────────────
async function initNative(): Promise<void> {
  const cap = (
    window as unknown as { Capacitor?: { getPlatform: () => string } }
  ).Capacitor;
  if (!cap || cap.getPlatform() === "web") return;

  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide({ fadeOutDuration: 300 });
    await syncNativeStatusBar();
  } catch {
    // No crítico — la app funciona igual sin estos ajustes
  }
}

// ── Reset de datos demo por versión ──────────────────────────────────────
// v19: fix - altenot_caja_user_real_pos se contaminaba con actividad de Auditoría.
//      Se limpia para restaurar datos demo de Caja en sesiones afectadas.
// v20: seed de logs de Auditoría por secciones (Caja, Ventas, Cierres, Movimientos,
//      Créditos, Fidelización, Inventario, Personal, Higiene) para el feed
//      personalizable de Actividad del Dashboard.
// v21: asistencia demo ampliada (toda la nómina, dos semanas, turnos abiertos y
//      cerrados de hoy, métodos QR/facial/carnet y evidencia facial de ejemplo).
const MOCK_DATA_VERSION = "v21";
import {
  txRead,
  txReadRaw,
  txWrite,
  txWriteRaw,
  txRemove,
} from "@/lib/persistence/transactionalStore";

/**
 * Productos creados por el usuario (los que no existen en el catálogo demo).
 * Deben sobrevivir al re-sembrado de datos demo por versión: son datos del
 * negocio, no datos de la demo.
 */
function readUserCreatedProducts(): unknown[] {
  try {
    const stored = txRead<unknown>("inventory_products", []);
    if (!Array.isArray(stored)) return [];
    const defaultIds = new Set(
      defaultProducts.map((p) => String((p as { id?: string }).id ?? "")),
    );
    return stored.filter(
      (p) =>
        !!p &&
        typeof p === "object" &&
        !defaultIds.has(String((p as { id?: string }).id ?? "")),
    );
  } catch {
    return [];
  }
}

/**
 * ¿El usuario ya tiene un inventario en el dispositivo? La existencia de la
 * clave (aunque sea un array vacío tras «Eliminar datos de prueba») significa
 * que el inventario es suyo — selección curada del demo o productos propios —
 * y debe sobrevivir a un reset de versión SIN re-sembrar los 363 productos demo.
 */
function inventoryKeyExists(): boolean {
  try {
    return txReadRaw("inventory_products") !== null;
  } catch {
    return false;
  }
}

function resetMockDataForVersionIfNeeded(): void {
  if (txReadRaw("mock_data_version") === MOCK_DATA_VERSION) return;
  if (hasUserRealCajaActivity()) {
    txWriteRaw("mock_data_version", MOCK_DATA_VERSION);
    return;
  }

  // Inventario curado (categorías demo elegidas, demo eliminado o productos
  // propios): un cambio de versión conserva el inventario y su historial de
  // movimientos tal cual. Solo se siembran los 363 productos demo en
  // instalación nueva (clave inexistente).
  if (inventoryKeyExists()) {
    MOCK_DATA_RESET_KEYS.forEach((k) => {
      if (k === "inventory_products" || k === "inventory_stock_movements") {
        return;
      }
      txRemove(k);
    });
    txWriteRaw("mock_data_version", MOCK_DATA_VERSION);
    try {
      // Re-sembra el resto de claves demo (empleados, tienda, fidelización…)
      // sin tocar el inventario conservado (ensureArray respeta arrays no vacíos).
      applyMockDemoBootstrap({ afterVersionReset: true, force: true });
    } catch (err) {
      logger.warn(
        "[demo-bootstrap] tras reset de versión con inventario conservado:",
        err,
      );
    }
    return;
  }

  // Conservar los productos creados por el usuario (incluidos los combos de
  // liquidación): son del negocio y no deben perderse al re-sembrar datos demo.
  const userProducts = readUserCreatedProducts();

  MOCK_DATA_RESET_KEYS.forEach((k) => txRemove(k));
  // La actividad POS real es independiente de los datos demo y debe
  // sobrevivir a un cambio de versión para que nunca se reinyecte demo.
  try {
    txRemove("altenot_real_activity_started");
    txRemove("altenot_caja_demo_fresh_applied");
  } catch {
    /* ignore */
  }
  txWriteRaw("mock_data_version", MOCK_DATA_VERSION);
  try {
    applyMockDemoBootstrap({ afterVersionReset: true, force: true });
    // Re-insertar los productos del usuario tras el reseed para que no se pierdan.
    if (userProducts.length > 0) {
      const reseeded = txRead<unknown>("inventory_products", []);
      if (Array.isArray(reseeded)) {
        txWrite("inventory_products", [...userProducts, ...reseeded]);
      }
    }
  } catch (err) {
    logger.warn("[demo-bootstrap] tras reset de versión:", err);
  }
}

function mountApp() {
  const rootElement = document.getElementById("root");
  if (!rootElement) throw new Error("No se encontró el contenedor #root");
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <HelmetProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <App />
          <ToastLimit />
          <ToastFitWidth />
          <Toaster
            position="top-right"
            containerClassName={APP_TOASTER_CLASS}
            toastOptions={appToasterOptions}
          >
            {(t) => (
              <ToastBar toast={t}>
                {({ icon, message }) => (
                  <>
                    {icon}
                    {message}
                    {t.type !== "loading" && (
                      <button
                        type="button"
                        onClick={() => toast.dismiss(t.id)}
                        aria-label="Cerrar notificación"
                        className="app-toast-close"
                      >
                        <X size={14} strokeWidth={2.5} />
                      </button>
                    )}
                  </>
                )}
              </ToastBar>
            )}
          </Toaster>
        </BrowserRouter>
      </HelmetProvider>
    </React.StrictMode>,
  );
}

async function bootstrap() {
  // 0º Pedir almacenamiento persistente: reduce la probabilidad de que
  // Android/Chrome evicten los datos locales por presión de espacio del SO.
  try {
    if (navigator.storage && typeof navigator.storage.persist === "function") {
      void navigator.storage.persist();
    }
  } catch (err) {
    logger.warn(
      "[persistence] no se pudo solicitar almacenamiento persistente:",
      err,
    );
  }

  // 1º Restaurar desde espejo IndexedDB si localStorage está vacío (kill de APK).
  //    Debe ocurrir ANTES de recoverStore para que el WAL encuentre las claves.
  try {
    await restoreFromIDBMirror();
  } catch (err) {
    logger.warn("[IDB Mirror] fallo en restauración:", err);
  }

  // 2º Durabilidad ACID: reaplica cualquier transacción local incompleta que haya
  // quedado por un cierre inesperado ANTES de leer o sembrar datos demo.
  try {
    recoverStore();
  } catch (err) {
    logger.warn("[persistence] recuperación de transacciones pendientes:", err);
  }

  // El reset solo puede ocurrir después de restaurar IndexedDB: en Android
  // localStorage puede iniciar vacío aunque el usuario ya tenga datos reales.
  resetMockDataForVersionIfNeeded();

  const cap = (
    window as unknown as { Capacitor?: { getPlatform: () => string } }
  ).Capacitor;
  const isCapacitorNative = !!cap && cap.getPlatform() !== "web";
  const mswDisabledOnNative =
    import.meta.env.VITE_DISABLE_MSW_NATIVE === "true";
  const mswDisabledGlobally = import.meta.env.VITE_DISABLE_MSW === "true";
  // Producción web/lab: MSW sirve inventario, tienda, auditoría demo (Cloud Functions
  // aún no expone /api/tienda). Desactivar solo cuando el backend real esté listo:
  // VITE_DISABLE_MSW=true
  const useMsw =
    !mswDisabledGlobally &&
    (import.meta.env.DEV ||
      import.meta.env.VITE_ENABLE_MSW === "true" ||
      import.meta.env.PROD ||
      (isCapacitorNative && !mswDisabledOnNative));

  if (useMsw) {
    // Auto-recuperación: si veníamos en bucle de recargas, el Service Worker de
    // MSW pudo quedar roto (típico tras actualización del WebView). Forzamos su
    // limpieza para que worker.start() registre uno limpio.
    const loopedRecently = isReloadLoopDetected();
    await unregisterConflictingServiceWorkers({ force: loopedRecently });
  }

  try {
    applyMockDemoBootstrap();
    applyCajaDemoFreshStart();
    // Recuperación defensiva ACID: si el historial de ventas/movimientos perdió
    // registros pero Auditoría los conserva, se reconstruyen (parciales) para
    // que totales, resúmenes y cierres nunca queden incompletos.
    reconstructMissingVentasFromAuditoria();
    reconstructMissingMovimientosFromAuditoria();
  } catch (err) {
    logger.warn(
      "[demo-bootstrap] applyMockDemoBootstrap / caja en bootstrap:",
      err,
    );
  }

  // 3º Integrity check: valida consistencia Auditoría ↔ Ventas ↔ Cierres.
  //    Fire-and-forget: no bloquea el montaje de la app.
  runIntegrityCheck().catch((err) =>
    logger.warn("[IntegrityCheck] fallo en chequeo de arranque:", err),
  );

  // Señal global de "calentamiento" de MSW: mientras el worker no esté activo,
  // las peticiones GET a /api/* se reintentan en api.ts (evita que la primera
  // carga se vaya a la red real → falle → "No pudimos cargar" → recarga en bucle).
  const w = window as Window & {
    __altenotMswEnabled?: boolean;
    __altenotMswReady?: boolean;
  };
  w.__altenotMswEnabled = useMsw;
  w.__altenotMswReady = !useMsw;

  // Montar SIEMPRE primero: garantiza que la app renderice aunque el mock
  // (MSW) tarde o falle al registrarse en el WebView. El mock arranca en
  // segundo plano y, cuando está listo, intercepta /api/*.
  mountApp();
  installApiConnectivityListeners();

  if (useMsw) {
    // Tope de seguridad: si el worker no activa nunca, terminamos el
    // calentamiento a los 8s para no reintentar peticiones indefinidamente.
    const warmupTimeout = setTimeout(() => {
      w.__altenotMswReady = true;
    }, 8000);

    void (async () => {
      const { worker } = await import("./mocks/browser");
      await worker.start({
        onUnhandledRequest: "bypass",
        quiet: true,
        waitUntilReady: false,
        serviceWorker: {
          url: "/mockServiceWorker.js",
          // Scope "/" necesario para que el SW controle las páginas de la SPA
          // (/dashboard, /inventario, /caja…) y pueda interceptar sus fetch a /api/*.
          // onUnhandledRequest:"bypass" ya evita interferir con navegación SPA.
          options: { scope: "/" },
        },
      });
      clearTimeout(warmupTimeout);
      w.__altenotMswReady = true;
      // Arranque sano: el mock intercepta /api/*. Reseteamos el guard anti-bucle.
      resetReloadLoopGuard();
      window.dispatchEvent(new CustomEvent("altenot:msw-ready"));
    })().catch((err) => {
      clearTimeout(warmupTimeout);
      w.__altenotMswReady = true;
      logger.warn("[MSW] No se pudo iniciar el mock en segundo plano:", err);
    });
  }

  await initNative();
}

bootstrap();

// ── PWA Service Worker ────────────────────────────────────────────────────────
// Registra sw.js para habilitar el botón "Instalar" en Chrome/Edge/Safari.
// Se registra DESPUÉS de bootstrap() para no bloquear el arranque de la app.
// No interfiere con mockServiceWorker.js (MSW gestiona /api/* con su propio scope).
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {
      // No crítico — la app funciona igual sin el SW PWA
    });
  });
}
